import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Wifi, Battery, Signal, Thermometer, MapPin, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Device } from '../types';
import deviceService from '../services/deviceService';
import DeviceTypeDetailsRenderer from '../components/DeviceTypeDetailsRenderer';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import NetatmoStationDetails from '../components/device-types/NetatmoStationDetails';
import NetatmoAnalyticsDashboard from '../components/NetatmoAnalyticsDashboard';
import {
  PageContainer,
  Header,
  BackButton,
  DeviceTitle,
  TitleSection,
  DeviceName,
  DeviceSubtitle,
  DeviceId,
  StatusSection,
  StatusBadge,
  LastUpdate,
  KeyMetrics,
  MetricCard,
  MetricLabel,
  MetricValue,
  MetricUnit,
  MetricChange,
  NavigationTabs,
  TabList,
  Tab,
  LoadingContainer,
  ErrorContainer
} from '../styles/DeviceDetailsStyles';

const DeviceDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [device, setDevice] = useState<Device | null>(null);
  const [stationModules, setStationModules] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchDevice = async () => {
      if (!id) {
        setError('Device ID not provided');
        setLoading(false);
        return;
      }

      try {
        setError(null);

        // For Netatmo weather stations, fetch using the stations endpoint to get all modules
        if (id) {
          const deviceData = await deviceService.getDevice(id);

          // Check if this is a Netatmo main station (NAMain module type, no parent)
          if (deviceData.netatmoModuleType === 'NAMain' && !deviceData.parentDeviceId) {
            try {
              // Use the stations endpoint to get the main station with all its modules
              const response = await fetch(`/api/stations/${id}`);
              if (response.ok) {
                const stationData = await response.json();
                // Backend returns { station, modules } format
                setDevice(stationData.station || stationData);
                setStationModules(stationData.modules || []);
                console.log('Fetched station with modules:', stationData);
              } else {
                // Fallback to device data without modules
                setDevice(deviceData);
                setStationModules([]);
              }
            } catch (err) {
              console.error('Error fetching station with modules:', err);
              // Fallback to device data without modules
              setDevice(deviceData);
              setStationModules([]);
            }
          } else {
            // For non-Netatmo devices, just use the device data
            setDevice(deviceData);
          }
        }
      } catch (err) {
        setError('Failed to fetch device details. Please try again.');
        console.error('Error fetching device:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDevice();
  }, [id]);

  const formatLastUpdate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${Math.floor(diffInMinutes)} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const formatCoordinates = (lat?: number, lng?: number) => {
    if (!lat || !lng) return 'Location unavailable';
    return `${lat.toFixed(6)}°, ${lng.toFixed(6)}°`;
  };

  const getStatusText = (status: 'online' | 'offline' | 'warning') => {
    switch (status) {
      case 'online': return 'Active';
      case 'warning': return 'Warning';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  const getTrendIcon = (trend?: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return <TrendingUp size={14} />;
      case 'down': return <TrendingDown size={14} />;
      default: return <Minus size={14} />;
    }
  };

  const getSignalQuality = (signalStrength?: number): { text: string; trend: 'up' | 'down' | 'stable' } => {
    if (signalStrength === undefined || signalStrength === 99) return { text: 'Unknown', trend: 'stable' };
    if (signalStrength === 0) return { text: 'No Signal', trend: 'down' };
    if (signalStrength >= 20) return { text: 'Excellent', trend: 'up' };
    if (signalStrength >= 15) return { text: 'Good', trend: 'up' };
    if (signalStrength >= 10) return { text: 'Fair', trend: 'stable' };
    if (signalStrength >= 5) return { text: 'Weak', trend: 'down' };
    return { text: 'Very Weak', trend: 'down' };
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          Loading device details...
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !device) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate('/devices')}>
          <ArrowLeft size={16} />
          Back to Devices
        </BackButton>
        <ErrorContainer>
          {error || 'Device not found'}
        </ErrorContainer>
      </PageContainer>
    );
  }

  // Determine if this is a Netatmo station that should show module view
  const isNetatmoStation = device && device.netatmoModuleType === 'NAMain' && !device.parentDeviceId;

  console.log('DeviceDetailsPage - Device:', device);
  console.log('DeviceDetailsPage - isNetatmoStation:', isNetatmoStation);
  console.log('DeviceDetailsPage - deviceType:', device?.deviceType);

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/devices')}>
        <ArrowLeft size={16} />
        Back to Devices
      </BackButton>

      <Header>
        <DeviceTitle>
          <TitleSection>
            <DeviceName>
              {device.name || `Marine Buoy ${device.id}`}
            </DeviceName>
            <DeviceSubtitle>
              {isNetatmoStation ? 'Weather Station' : 'Maritime Sensor Device'}
            </DeviceSubtitle>
            {isNetatmoStation && stationModules.length > 0 && (
              <DeviceId>
                {stationModules.length + 1} connected modules
              </DeviceId>
            )}
            {!isNetatmoStation && (
              <DeviceId>
                ID: {device.deviceId}
              </DeviceId>
            )}
          </TitleSection>

          <StatusSection>
            <StatusBadge status={device.status}>
              {getStatusText(device.status)}
            </StatusBadge>
            <LastUpdate>
              Last update: {device.lastMessageAt ? formatLastUpdate(device.lastMessageAt) : 'Never'}
            </LastUpdate>
          </StatusSection>
        </DeviceTitle>

        {/* Show key metrics only for non-station devices */}
        {!isNetatmoStation && (
          <KeyMetrics>
            <MetricCard>
              <MetricLabel>
                <Battery size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Battery Level
              </MetricLabel>
              <MetricValue>
                {device.batteryLevel ?? '--'}
                <MetricUnit>%</MetricUnit>
              </MetricValue>
              <MetricChange trend={device.batteryLevel && device.batteryLevel > 80 ? 'up' : device.batteryLevel && device.batteryLevel < 20 ? 'down' : 'stable'}>
                {getTrendIcon(device.batteryLevel && device.batteryLevel > 80 ? 'up' : device.batteryLevel && device.batteryLevel < 20 ? 'down' : 'stable')}
                {device.batteryLevel ? (device.batteryLevel > 80 ? 'Good' : device.batteryLevel < 20 ? 'Low' : 'Normal') : 'Unknown'}
              </MetricChange>
            </MetricCard>

            <MetricCard>
              <MetricLabel>
                <Signal size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Signal Strength
              </MetricLabel>
              <MetricValue>
                {device.signalStrength ?? '--'}
                <MetricUnit>/31</MetricUnit>
              </MetricValue>
              <MetricChange trend={device.signalStrength && device.signalStrength >= 15 ? 'up' : device.signalStrength && device.signalStrength <= 10 ? 'down' : 'stable'}>
                {getTrendIcon(device.signalStrength && device.signalStrength >= 15 ? 'up' : device.signalStrength && device.signalStrength <= 10 ? 'down' : 'stable')}
                {getSignalQuality(device.signalStrength).text}
              </MetricChange>
            </MetricCard>

            <MetricCard>
              <MetricLabel>
                <Thermometer size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Temperature
              </MetricLabel>
              <MetricValue>
                {device.temperature ? device.temperature.toFixed(1) : '--'}
                <MetricUnit>°C</MetricUnit>
              </MetricValue>
              <MetricChange trend="stable">
                {getTrendIcon('stable')}
                Normal range
              </MetricChange>
            </MetricCard>

            <MetricCard>
              <MetricLabel>
                <MapPin size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Location
              </MetricLabel>
              <MetricValue style={{ fontSize: '16px' }}>
                {formatCoordinates(device.latitude, device.longitude)}
              </MetricValue>
              <MetricChange trend="stable">
                {getTrendIcon('stable')}
                GPS Active
              </MetricChange>
            </MetricCard>

            <MetricCard>
              <MetricLabel>
                <Wifi size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Connection Status
              </MetricLabel>
              <MetricValue style={{ fontSize: '18px', color: device.status === 'online' ? '#10b981' : device.status === 'warning' ? '#f59e0b' : '#ef4444' }}>
                {getStatusText(device.status)}
              </MetricValue>
              <MetricChange trend={device.status === 'online' ? 'up' : device.status === 'warning' ? 'stable' : 'down'}>
                {getTrendIcon(device.status === 'online' ? 'up' : device.status === 'warning' ? 'stable' : 'down')}
                {device.status === 'online' ? 'Connected' : device.status === 'warning' ? 'Intermittent' : 'Disconnected'}
              </MetricChange>
            </MetricCard>

            <MetricCard>
              <MetricLabel>Days Since Deployment</MetricLabel>
              <MetricValue>
                {Math.floor((new Date().getTime() - new Date(device.createdAt).getTime()) / (1000 * 60 * 60 * 24))}
                <MetricUnit>days</MetricUnit>
              </MetricValue>
              <MetricChange trend="up">
                {getTrendIcon('up')}
                Since first registered
              </MetricChange>
            </MetricCard>
          </KeyMetrics>
        )}
      </Header>

      <NavigationTabs>
        <TabList>
          <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            Overview
          </Tab>
          <Tab active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>
            Analytics
          </Tab>
        </TabList>
      </NavigationTabs>

      {/* Content area for different tabs */}
      {activeTab === 'overview' && (
        <>
          {isNetatmoStation ? (
            <NetatmoStationDetails
              mainStation={device}
              modules={stationModules}
            />
          ) : (
            <DeviceTypeDetailsRenderer device={device} deviceId={id!} />
          )}
        </>
      )}

      {activeTab === 'analytics' && (
        isNetatmoStation ? (
          <NetatmoAnalyticsDashboard
            mainStation={device}
            modules={stationModules}
          />
        ) : (
          <AnalyticsDashboard device={device} />
        )
      )}
    </PageContainer>
  );
};

export default DeviceDetailsPage;
